import React from 'react';
import {
  BrainCircuit, GitCompareArrows, Network, ShieldCheck,
} from 'lucide-react';
import {
  siPython, siTypescript, siReact, siNextdotjs,
  siTailwindcss, siNumpy, siGit, siDocker, siLinux,
  siOpensearch, siPrometheus, siGithubactions,
} from 'simple-icons';

export function About() {
  const projects = [
    {
      name: 'meridian',
      desc: 'News aggregator that clusters articles across outlets using Qwen3 embeddings and DBSCAN. Bias-tags each source and serves a real-time D3.js story graph, with no API keys.',
      tags: ['python', 'embeddings', 'nlp'],
      link: 'https://meridian.matifema.com',
    },
    {
      name: 'OWDTools',
      desc: 'MCP server that brings Our World in Data directly into LLM chats. Semantic catalog search, interactive chart embeds, and raw data extraction for hundreds of global datasets.',
      tags: ['python', 'mcp', 'data'],
      link: 'https://github.com/matifema/OWDTools',
    },
    {
      name: 'opendex',
      desc: 'Little mobile app that turns anything you take a picture of into a gen-1 pokémon.',
      tags: ['python', 'ml', 'fun'],
      link: 'https://github.com/matifema/opendex',
    },
  ];

  const skills = [
    ['python', 'typescript', 'react', 'next.js', 'tailwind'],
    ['vector embeddings', 'semantic search', 'opensearch', 'prometheus', 'numpy'],
    ['git', 'docker', 'linux'],
    ['reliability engineering', 'ci/cd', 'matching algorithms'],
  ];

  // Helper to render simple-icons as inline SVG
  const Si = ({ icon, size = 14 }: { icon: typeof siPython; size?: number }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
      <path d={icon.path} />
    </svg>
  );

  // Icons for each skill (shown on flip, matches chip size)
  const skillLogos: Record<string, React.ReactNode> = {
    python: <Si icon={siPython} />,
    typescript: <Si icon={siTypescript} />,
    react: <Si icon={siReact} />,
    'next.js': <Si icon={siNextdotjs} />,
    tailwind: <Si icon={siTailwindcss} />,
    'vector embeddings': <Network size={14} />,
    'semantic search': <BrainCircuit size={14} />,
    opensearch: <Si icon={siOpensearch} />,
    prometheus: <Si icon={siPrometheus} />,
    numpy: <Si icon={siNumpy} />,
    git: <Si icon={siGit} />,
    docker: <Si icon={siDocker} />,
    linux: <Si icon={siLinux} />,
    'reliability engineering': <ShieldCheck size={14} />,
    'ci/cd': <Si icon={siGithubactions} />,
    'matching algorithms': <GitCompareArrows size={14} />,
  };

  return (
    <section id="work" style={{ padding: '1.5rem 1.5rem 3rem 1.5rem', maxWidth: '42rem', margin: '0 auto' }}>
      {/* ── Projects ── */}
      <h2 style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 500,
        fontSize: '1.25rem',
        letterSpacing: '-0.02em',
        margin: '0 0 1.5rem 0',
        color: 'var(--charcoal)',
      }}>
        projects
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '3rem' }}>
        {projects.map((project) => (
          <div 
            key={project.name}
            style={{
              borderLeft: '2px solid var(--rule)',
              paddingLeft: '1rem',
            }}
          >
            <h3 style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 500,
              fontSize: '0.9375rem',
              margin: '0 0 0.25rem 0',
            }}>
              <a 
                href={project.link} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: 'var(--charcoal)', textDecoration: 'none' }}
              >
                {project.name}
              </a>
            </h3>
            <p style={{
              fontSize: '0.9375rem',
              lineHeight: 1.6,
              color: 'var(--muted)',
              margin: '0 0 0.375rem 0',
            }}>
              {project.desc}
            </p>
            <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
              {project.tags.map((tag) => (
                <span key={tag} style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.5625rem',
                  color: 'var(--muted)',
                }}>
                  [{tag}]
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <hr />

      {/* ── Skills ── */}
      <h2 style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 500,
        fontSize: '1.25rem',
        letterSpacing: '-0.02em',
        margin: '0 0 1.25rem 0',
        color: 'var(--charcoal)',
      }}>
        skills
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', marginBottom: '3rem' }}>
        {skills.map((group, i) => (
          <div key={i} style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.75rem',
            color: 'var(--charcoal)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            {group.map((skill) => (
              <span
                key={skill}
                className="skill-flip"
                style={{
                  display: 'inline-block',
                  perspective: '300px',
                  cursor: 'default',
                }}
              >
                <span
                  className="skill-flip-inner"
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    transition: 'transform 0.45s ease',
                    transformStyle: 'preserve-3d',
                  }}
                >
                  {/* invisible spacer to hold size */}
                  <span style={{
                    visibility: 'hidden',
                    border: '1px solid transparent',
                    padding: '0.125rem 0.5rem',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.75rem',
                  }}>
                    {skill}
                  </span>
                  <span style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid var(--border)',
                    padding: '0.125rem 0.5rem',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    background: 'var(--paper)',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.75rem',
                    color: 'var(--charcoal)',
                  }}>
                    {skill}
                  </span>
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid var(--indigo)',
                      padding: '0.125rem 0.5rem',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      color: 'var(--indigo)',
                      background: 'var(--paper)',
                    }}
                  >
                    {skillLogos[skill]}
                  </span>
                </span>
              </span>
            ))}
          </div>
        ))}
      </div>

      <hr />

      {/* ── Education ── */}
      <h2 style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 500,
        fontSize: '1.25rem',
        letterSpacing: '-0.02em',
        margin: '0 0 1.25rem 0',
        color: 'var(--charcoal)',
      }}>
        education
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '3rem' }}>
        <div style={{ borderLeft: '2px solid var(--rule)', paddingLeft: '1rem' }}>
          <h3 style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 500,
            fontSize: '0.875rem',
            margin: '0 0 0.125rem 0',
            color: 'var(--charcoal)'
          }}>
            BSc Computer Science
          </h3>
          <p style={{
            fontSize: '0.875rem',
            color: 'var(--indigo)',
            margin: '0 0 0.25rem 0',
          }}>
            Sapienza Università di Roma
          </p>
          <p style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.625rem',
            color: 'var(--muted)',
            margin: '0 0 0.5rem 0',
          }}>
            2023 – expected 2026
          </p>
          <p style={{
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: 'var(--muted)',
            margin: 0,
          }}>
            Software engineering, algorithms & data structures, system design, AI verification.
          </p>
        </div>

        <div style={{ borderLeft: '2px solid var(--rule)', paddingLeft: '1rem' }}>
          <h3 style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 500,
            fontSize: '0.875rem',
            margin: '0 0 0.125rem 0',
            color: 'var(--charcoal)'
          }}>
            Full-Stack Development Programme
          </h3>
          <p style={{
            fontSize: '0.875rem',
            color: 'var(--indigo)',
            margin: '0 0 0.25rem 0',
          }}>
            Generation Italy
          </p>
          <p style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.625rem',
            color: 'var(--muted)',
            margin: '0 0 0.5rem 0',
          }}>
            2022
          </p>
          <p style={{
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: 'var(--muted)',
            margin: 0,
          }}>
            Intensive bootcamp on Java, Spring Boot, HTML, CSS, JavaScript. Delivered full-stack project in 4 weeks.
          </p>
        </div>
      </div>

      <hr />

      {/* ── Contact ── */}
      <h2 style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 500,
        fontSize: '1.25rem',
        letterSpacing: '-0.02em',
        margin: '0 0 1rem 0',
        color: 'var(--charcoal)',
      }}>
        contact
      </h2>

      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.75rem',
        lineHeight: 2,
        color: 'var(--muted)',
        marginBottom: '1rem'
      }}>
        <div>roma, italy</div>
        <div>
          <a href="mailto:marcoferri462@gmail.com">marcoferri462@gmail.com</a>
        </div>
        <div>
          <a href="https://linkedin.com/in/marco-t-ferri-marini-3706901a3" target="_blank" rel="noopener noreferrer">
            linkedin
          </a>
        </div>
        <div>
          <a href="https://github.com/matifema" target="_blank" rel="noopener noreferrer">
            github
          </a>
        </div>
      </div>

      <p style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.625rem',
        color: 'var(--muted)',
        marginTop: '1rem',
        fontStyle: 'italic'
      }}>
        pgp: <a href="/pgp-key.asc" style={{ fontSize: '0.625rem' }}>63A5 6D10 F29F C1C3 1B7E 2B5E 9F84 3C6D A12E 8D4F</a>
      </p>
    </section>
  );
}
